import React, { useState, useMemo, useEffect } from "react";
import { 
  TrendingUp, 
  TrendingDown, 
  ShieldCheck, 
  Target, 
  Clock, 
  CheckCircle2, 
  BarChart3, 
  Award, 
  Flame, 
  Zap, 
  Layers, 
  CalendarRange, 
  ArrowRight, 
  Lock, 
  History, 
  Settings,
  Crosshair 
} from "lucide-react";
import { formatDisplayTime } from "../services/timezoneService";
import { useAuth } from "../contexts/AuthContext";
import TradeHistoryModal from "./TradeHistoryModal";

export const formatPrice = (val, maxDec = null) => {
  if (val === null || val === undefined || val === "") return "-";
  const num = Number(val);
  if (isNaN(num)) return String(val);
  if (maxDec === null) {
    maxDec = num < 5.0 ? 5 : (num < 500.0 ? 3 : 2);
  }
  const str = String(val);
  if (str.includes(".") && str.split(".")[1].length > maxDec) {
    return Number(num.toFixed(maxDec));
  }
  return val;
};

export default function SignalPanel({ 
  signals, 
  smc, 
  hmaSystem,
  bobVolman,
  orbSession,
  liquiditySweep,
  scalpingPro,
  strategyMode: propStrategyMode,
  onSelectStrategyMode,
  onOpenStrategySettings,
  activeIndicatorIds,
  onOpenIndicatorLibrary,
  currentSymbol, 
  currentTimeframe, 
  currentTimezone = "local", 
  candleCount = 10000,
  onWorkspaceChange,
  restoredWorkspace,
  strategyParams,
  onApplyStrategy
}) {
  const { canAccess, isBasic, isPro, isAdmin, promptTierGate, openAuth } = useAuth();
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [internalStrategyMode, setInternalStrategyMode] = useState(() => {
    try {
      return localStorage.getItem("minopro_signal_strategy_mode") || "hma";
    } catch (e) {
      return "hma";
    }
  });

  const strategyMode = propStrategyMode || internalStrategyMode;

  const SYSTEM_TABS = useMemo(() => [
    {
      id: "msb_dynamic",
      mode: "smc",
      name: "SMC Pro",
      icon: Layers,
      activeBg: "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40 shadow-sm shadow-indigo-500/20 font-bold",
      iconColor: "text-indigo-400",
      adminOnly: false,
    },
    {
      id: "hma_breakout_system",
      mode: "hma",
      name: "HMA Pro",
      icon: Zap,
      activeBg: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-sm shadow-emerald-500/20 font-bold",
      iconColor: "text-emerald-400",
      adminOnly: false,
    },
    {
      id: "bob_volman_system",
      mode: "bob_volman",
      name: "Bob Volman",
      icon: Zap,
      activeBg: "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-sm shadow-cyan-500/20 font-bold",
      iconColor: "text-cyan-400",
      adminOnly: false,
    },
    {
      id: "liquidity_sweep_pro",
      mode: "liquidity_sweep",
      name: "Sweep Pro (PA)",
      icon: Crosshair,
      activeBg: "bg-violet-500/20 text-violet-300 border border-violet-500/40 shadow-sm shadow-violet-500/20 font-bold",
      iconColor: "text-violet-400",
      adminOnly: false,
    },
    {
      id: "scalping_pro_system",
      mode: "scalping_pro",
      name: "Scalping Pro",
      icon: Zap,
      activeBg: "bg-rose-500/20 text-rose-300 border border-rose-500/40 shadow-sm shadow-rose-500/20 font-bold",
      iconColor: "text-rose-400",
      adminOnly: false,
    },
    {
      id: "orb_session_system",
      mode: "orb_session",
      name: "ORB Session",
      icon: Clock,
      activeBg: "bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-sm shadow-amber-500/20 font-bold",
      iconColor: "text-amber-400",
      adminOnly: true,
    },
    {
      id: "matrix_pro",
      mode: "matrix",
      name: "Matrix Pro",
      icon: Zap,
      activeBg: "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-sm shadow-cyan-500/20 font-bold",
      iconColor: "text-cyan-400",
      adminOnly: false,
    },
  ], []);

  const effectiveIndicatorIds = useMemo(() => {
    if (Array.isArray(activeIndicatorIds)) return activeIndicatorIds;
    try {
      const saved = JSON.parse(localStorage.getItem("minopro_active_indicators") || '[]');
      if (Array.isArray(saved) && saved.length > 0) return saved;
    } catch (e) {}
    return ["hma_breakout_system"];
  }, [activeIndicatorIds]);

  const visibleSystems = useMemo(() => {
    return SYSTEM_TABS.filter((sys) => {
      if (sys.adminOnly && !isAdmin) return false;
      return effectiveIndicatorIds.includes(sys.id);
    });
  }, [SYSTEM_TABS, effectiveIndicatorIds, isAdmin]);

  // Fallback for non-admin accounts and auto-switching when current system is removed
  useEffect(() => {
    if (!isAdmin && strategyMode === "orb_session") {
      const fallbackMode = visibleSystems[0]?.mode || "hma";
      handleSetStrategyMode(fallbackMode);
    } else if (visibleSystems.length > 0 && !visibleSystems.some((s) => s.mode === strategyMode)) {
      handleSetStrategyMode(visibleSystems[0].mode);
    }
  }, [isAdmin, strategyMode, visibleSystems]);

  const [activeTab, setActiveTab] = useState(() => {
    try {
      return localStorage.getItem("minopro_signal_active_tab") || "signal";
    } catch (e) {
      return "signal";
    }
  });
  const [timeFilter, setTimeFilter] = useState(() => {
    try {
      return localStorage.getItem("minopro_signal_time_filter") || "all";
    } catch (e) {
      return "all";
    }
  });

  // Restore state from cloud workspace
  useEffect(() => {
    if (restoredWorkspace) {
      if (restoredWorkspace.strategyMode) {
        setInternalStrategyMode(restoredWorkspace.strategyMode);
        if (onSelectStrategyMode) onSelectStrategyMode(restoredWorkspace.strategyMode);
      }
      if (restoredWorkspace.activeTab) {
        setActiveTab(restoredWorkspace.activeTab);
      }
      if (restoredWorkspace.timeFilter) {
        setTimeFilter(restoredWorkspace.timeFilter);
      }
    }
  }, [restoredWorkspace]);

  const handleSetStrategyMode = (mode) => {
    setInternalStrategyMode(mode);
    try {
      localStorage.setItem("minopro_signal_strategy_mode", mode);
    } catch (e) {}
    if (onSelectStrategyMode) {
      onSelectStrategyMode(mode);
    }
    if (onWorkspaceChange) {
      onWorkspaceChange({ strategyMode: mode });
    }
  };

  const handleSetActiveTab = (tab) => {
    setActiveTab(tab);
    try {
      localStorage.setItem("minopro_signal_active_tab", tab);
    } catch (e) {}
    if (onWorkspaceChange) {
      onWorkspaceChange({ activeTab: tab });
    }
  };

  const handleSetTimeFilter = (filter) => {
    setTimeFilter(filter);
    try {
      localStorage.setItem("minopro_signal_time_filter", filter);
    } catch (e) {}
    if (onWorkspaceChange) {
      onWorkspaceChange({ timeFilter: filter });
    }
  };

  // Custom Date Range State (YYYY-MM-DD)
  const todayStr = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  // Active Strategy Dataset
  const activeSource = useMemo(() => {
    if (strategyMode === "scalping_pro") return scalpingPro || null;
    if (strategyMode === "liquidity_sweep") return liquiditySweep || null;
    if (strategyMode === "orb_session") return orbSession || null;
    if (strategyMode === "bob_volman") return bobVolman || null;
    if (strategyMode === "smc") return smc?.signals || smc || null;
    if (strategyMode === "hma") return hmaSystem || null;
    return signals || null;
  }, [strategyMode, scalpingPro, liquiditySweep, orbSession, bobVolman, smc, hmaSystem, signals]);

  const latestSig = activeSource?.latest_signal || activeSource?.latestSignal;
  const allSignals = activeSource?.all_signals || activeSource?.signals || activeSource?.recent_signals || [];
  const rawSignals = activeSource?.recent_signals || allSignals;
  const streaks = activeSource?.streaks || { max_win_streak: 0, max_loss_streak: 0 };
  const orders = smc?.orders || [];

  const isBuy = latestSig?.action === "BUY";
  const isSell = latestSig?.action === "SELL";

  // Standard Pip Multiplier
  const getPipMultiplier = (sym) => {
    if (sym === "EURUSD" || sym === "GBPUSD") return 10000;
    if (sym === "USDJPY") return 100;
    if (sym === "XAUUSD") return 10;
    return 1;
  };

  const pipMult = getPipMultiplier(currentSymbol);

  // Helper: compute streak metrics from finished trades
  const getTradeStreaks = (tradesList) => {
    let maxW = 0, maxL = 0, curW = 0, curL = 0;
    (tradesList || []).forEach((t) => {
      const isWin = t.win === true || ["WIN_TP", "WIN_TP1", "WIN_TP2", "TP1_HIT", "TP2_HIT", "BE_WIN"].includes(t.status) || (t.pnl_pips ?? t.pnlPips ?? 0) > 0;
      const isLoss = t.win === false || ["LOSS_SL", "SL_HIT"].includes(t.status) || (t.pnl_pips ?? t.pnlPips ?? 0) < 0;
      if (isWin) {
        curW++;
        curL = 0;
        if (curW > maxW) maxW = curW;
      } else if (isLoss) {
        curL++;
        curW = 0;
        if (curL > maxL) maxL = curL;
      } else {
        curW = 0;
        curL = 0;
      }
    });
    return { max_win_streak: maxW, max_loss_streak: maxL };
  };

  // Helper: filter trades pool for predefined windows as a resilient fallback
  const getTradesForWindow = (filterName) => {
    const latestTs = allSignals[allSignals.length - 1]?.time || (Date.now() / 1000);
    let start = 0;
    if (filterName === "today" || filterName === "24h") {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      start = d.getTime() / 1000;
    } else if (filterName === "3d") {
      start = latestTs - 3 * 86400;
    } else if (filterName === "7d") {
      start = latestTs - 7 * 86400;
    } else if (filterName === "30d") {
      start = latestTs - 30 * 86400;
    }
    const pool = (activeSource?.backtest?.trades && activeSource.backtest.trades.length > 0)
      ? activeSource.backtest.trades
      : allSignals;
    return pool.filter((t) => (t.time ?? t.entry_time ?? t.fill_time ?? 0) >= start);
  };

  // Filter Backtest by Preset Window OR Custom Date Range
  const activeBacktest = useMemo(() => {
    // 1. Direct match in precomputed windows
    if (timeFilter !== "custom" && activeSource?.backtest_windows?.[timeFilter]) {
      const bw = activeSource.backtest_windows[timeFilter];
      const hasDefinedStreaks = (
        bw.max_win_streak !== undefined ||
        bw.max_loss_streak !== undefined ||
        bw.streaks?.max_win_streak !== undefined ||
        bw.streaks?.max_loss_streak !== undefined
      );

      let winStreaks;
      if (hasDefinedStreaks) {
        const wStreak = bw.max_win_streak ?? bw.streaks?.max_win_streak ?? 0;
        const lStreak = bw.max_loss_streak ?? bw.streaks?.max_loss_streak ?? 0;
        // If streaks are zero despite having trades, recompute from actual trades list
        if (wStreak === 0 && lStreak === 0 && ((bw.wins || 0) + (bw.losses || 0) > 0)) {
          const wTrades = Array.isArray(bw.trades) ? bw.trades : getTradesForWindow(timeFilter);
          winStreaks = getTradeStreaks(wTrades);
        } else {
          winStreaks = { max_win_streak: wStreak, max_loss_streak: lStreak };
        }
      } else {
        const wTrades = Array.isArray(bw.trades) ? bw.trades : getTradesForWindow(timeFilter);
        winStreaks = getTradeStreaks(wTrades);
      }

      return {
        ...bw,
        total_trades: bw.total_trades ?? bw.trades ?? ((bw.wins || 0) + (bw.losses || 0)),
        closed_trades: bw.closed_trades ?? ((bw.wins || 0) + (bw.losses || 0)),
        win_rate: bw.win_rate ?? bw.winRate ?? 0,
        profit_factor: bw.profit_factor ?? bw.profitFactor ?? 1.0,
        net_pnl: bw.net_pnl ?? bw.netPips ?? 0,
        max_win_streak: winStreaks.max_win_streak ?? 0,
        max_loss_streak: winStreaks.max_loss_streak ?? 0,
        streaks: winStreaks,
        avg_rr: activeSource?.backtest?.avg_rr || ((strategyMode === "liquidity_sweep" || strategyMode === "scalping_pro") ? "1:2.0" : "1:1.0")
      };
    }
    if ((timeFilter === "today" || timeFilter === "24h") && (activeSource?.backtest_windows?.["today"] || activeSource?.backtest_windows?.["24h"])) {
      const bw = activeSource.backtest_windows["today"] || activeSource.backtest_windows["24h"];
      const hasDefinedStreaks = (
        bw.max_win_streak !== undefined ||
        bw.max_loss_streak !== undefined ||
        bw.streaks?.max_win_streak !== undefined ||
        bw.streaks?.max_loss_streak !== undefined
      );

      let winStreaks;
      if (hasDefinedStreaks) {
        const wStreak = bw.max_win_streak ?? bw.streaks?.max_win_streak ?? 0;
        const lStreak = bw.max_loss_streak ?? bw.streaks?.max_loss_streak ?? 0;
        if (wStreak === 0 && lStreak === 0 && ((bw.wins || 0) + (bw.losses || 0) > 0)) {
          const wTrades = Array.isArray(bw.trades) ? bw.trades : getTradesForWindow("today");
          winStreaks = getTradeStreaks(wTrades);
        } else {
          winStreaks = { max_win_streak: wStreak, max_loss_streak: lStreak };
        }
      } else {
        const wTrades = Array.isArray(bw.trades) ? bw.trades : getTradesForWindow("today");
        winStreaks = getTradeStreaks(wTrades);
      }

      return {
        ...bw,
        total_trades: bw.total_trades ?? bw.trades ?? ((bw.wins || 0) + (bw.losses || 0)),
        closed_trades: bw.closed_trades ?? ((bw.wins || 0) + (bw.losses || 0)),
        win_rate: bw.win_rate ?? bw.winRate ?? 0,
        profit_factor: bw.profit_factor ?? bw.profitFactor ?? 1.0,
        net_pnl: bw.net_pnl ?? bw.netPips ?? 0,
        max_win_streak: winStreaks.max_win_streak ?? 0,
        max_loss_streak: winStreaks.max_loss_streak ?? 0,
        streaks: winStreaks,
        avg_rr: activeSource?.backtest?.avg_rr || ((strategyMode === "liquidity_sweep" || strategyMode === "scalping_pro") ? "1:2.0" : "1:1.0")
      };
    }
    if (timeFilter === "all" && activeSource?.backtest) {
      const ab = activeSource.backtest;
      const hasDefinedStreaks = (
        ab.max_win_streak !== undefined ||
        ab.max_loss_streak !== undefined ||
        ab.streaks?.max_win_streak !== undefined ||
        ab.streaks?.max_loss_streak !== undefined
      );

      let allStreaks;
      if (hasDefinedStreaks) {
        const wStreak = ab.max_win_streak ?? ab.streaks?.max_win_streak ?? streaks.max_win_streak ?? 0;
        const lStreak = ab.max_loss_streak ?? ab.streaks?.max_loss_streak ?? streaks.max_loss_streak ?? 0;
        if (wStreak === 0 && lStreak === 0 && ((ab.wins || 0) + (ab.losses || 0) > 0)) {
          const aTrades = Array.isArray(ab.trades) ? ab.trades : allSignals;
          allStreaks = getTradeStreaks(aTrades);
        } else {
          allStreaks = { max_win_streak: wStreak, max_loss_streak: lStreak };
        }
      } else {
        allStreaks = getTradeStreaks(Array.isArray(ab.trades) ? ab.trades : allSignals);
      }

      return {
        ...ab,
        total_trades: ab.total_trades ?? ab.trades ?? ((ab.wins || 0) + (ab.losses || 0)),
        closed_trades: ab.closed_trades ?? ((ab.wins || 0) + (ab.losses || 0)),
        win_rate: ab.win_rate ?? ab.winRate ?? 0,
        profit_factor: ab.profit_factor ?? ab.profitFactor ?? 1.0,
        net_pnl: ab.net_pnl ?? ab.netPips ?? 0,
        max_win_streak: allStreaks.max_win_streak ?? 0,
        max_loss_streak: allStreaks.max_loss_streak ?? 0,
        streaks: allStreaks,
        avg_rr: ab.avg_rr || ((strategyMode === "liquidity_sweep" || strategyMode === "scalping_pro") ? "1:2.0" : "1:1.0")
      };
    }

    if (!allSignals || allSignals.length === 0) {
      const ab = activeSource?.backtest;
      return {
        total_trades: ab?.total_trades ?? ab?.trades ?? 0,
        win_rate: ab?.win_rate ?? ab?.winRate ?? 0,
        wins: ab?.wins ?? 0,
        losses: ab?.losses ?? 0,
        net_pnl: ab?.net_pnl ?? ab?.netPips ?? 0,
        profit_factor: ab?.profit_factor ?? ab?.profitFactor ?? 1.0,
        avg_rr: ab?.avg_rr || ((strategyMode === "liquidity_sweep" || strategyMode === "scalping_pro") ? "1:2.0" : "1:1.0")
      };
    }

    // 2. Dynamic time filter computation
    const latestSignalTs = allSignals[allSignals.length - 1]?.time || (Date.now() / 1000);
    let startTs = 0;
    let endTs = Infinity;

    if (timeFilter === "custom") {
      // Timezone-safe start and end timestamps (aligned to UTC+7 for VN chart sessions)
      const tzOffsetHours = (currentTimezone === "Asia/Ho_Chi_Minh" || !currentTimezone) ? 7 : 7;
      const [sY, sM, sD] = startDate.split("-").map(Number);
      const [eY, eM, eD] = endDate.split("-").map(Number);
      startTs = Math.floor(Date.UTC(sY, sM - 1, sD, 0, 0, 0) / 1000) - tzOffsetHours * 3600;
      endTs = Math.floor(Date.UTC(eY, eM - 1, eD, 23, 59, 59) / 1000) - tzOffsetHours * 3600;
    } else if (timeFilter === "today" || timeFilter === "24h") {
      const todayDate = new Date();
      todayDate.setHours(0, 0, 0, 0);
      startTs = todayDate.getTime() / 1000;
    } else if (timeFilter === "3d") {
      startTs = latestSignalTs - 3 * 24 * 3600;
    } else if (timeFilter === "7d") {
      startTs = latestSignalTs - 7 * 24 * 3600;
    } else if (timeFilter === "30d") {
      startTs = latestSignalTs - 30 * 24 * 3600;
    } else if (timeFilter === "all") {
      startTs = 0;
    }

    const validClosedStatuses = new Set([
      "WIN_TP", "WIN_TP1", "WIN_TP2", "WIN_OPPOSITE", 
      "LOSS_SL", "LOSS_OPPOSITE", "CLOSED_OPPOSITE", 
      "TP1_HIT", "TP2_HIT", "SL_HIT", 
      "BREAKEVEN", "BE_WIN", "BE_OPPOSITE", "TIMEOUT", 
      "SESSION_CLOSE", "SESSION_TIMEOUT", "CLOSED", "RESOLVED"
    ]);

    const activeTradesPool = (activeSource?.backtest?.trades && activeSource.backtest.trades.length > 0)
      ? activeSource.backtest.trades
      : allSignals;

    const filtered = activeTradesPool.filter((s) => {
      const sTime = s.time ?? s.entry_time ?? s.fill_time ?? s.startTime ?? 0;
      if (sTime < startTs || sTime > endTs) return false;
      const isKnownClosed = validClosedStatuses.has(s.status);
      const isRunning = ["RUNNING", "PENDING_LIMIT", "PENDING", "ACTIVE"].includes(s.status);
      const hasResolvedPnl = s.exit_time || s.pnl_pips !== undefined || s.pnl_diff !== undefined || s.exit_price !== undefined;
      return isKnownClosed || (!isRunning && hasResolvedPnl);
    });

    let wins = 0;
    let losses = 0;
    let beCount = 0;
    let netPnlRaw = 0.0;
    let grossProfitRaw = 0.0;
    let grossLossRaw = 0.0;
    let maxWinStreak = 0;
    let maxLossStreak = 0;
    let curWin = 0;
    let curLoss = 0;

    filtered.forEach((s) => {
      const pnl = s.pnl_diff !== undefined ? s.pnl_diff : ((s.pnl_pips ?? s.pnlPips ?? 0) / pipMult);
      if (pnl > 0 || s.status === "WIN_TP" || s.status === "WIN_TP1" || s.status === "WIN_TP2" || s.status === "TP1_HIT" || s.status === "TP2_HIT") {
        wins += 1;
        grossProfitRaw += pnl;
        curWin += 1;
        curLoss = 0;
        if (curWin > maxWinStreak) maxWinStreak = curWin;
      } else if (pnl < 0 || s.status === "LOSS_SL" || s.status === "SL_HIT") {
        losses += 1;
        grossLossRaw += Math.abs(pnl);
        curLoss += 1;
        curWin = 0;
        if (curLoss > maxLossStreak) maxLossStreak = curLoss;
      } else {
        beCount += 1;
        curWin = 0;
        curLoss = 0;
      }
      netPnlRaw += pnl;
    });

    const totalTrades = wins + losses + beCount;
    const winRate = (wins + losses) > 0 ? Math.round((wins / (wins + losses)) * 1000) / 10 : 0;
    const profitFactor = grossLossRaw > 0 ? Math.round((grossProfitRaw / grossLossRaw) * 100) / 100 : (grossProfitRaw > 0 ? 99.0 : 1.0);
    const netPips = Math.round(netPnlRaw * pipMult * 10) / 10;

    return {
      total_trades: totalTrades,
      win_rate: winRate,
      wins,
      losses,
      net_pnl: netPips,
      profit_factor: profitFactor,
      max_win_streak: maxWinStreak,
      max_loss_streak: maxLossStreak,
      streaks: { max_win_streak: maxWinStreak, max_loss_streak: maxLossStreak },
      avg_rr: activeSource?.backtest?.avg_rr || "1:1.0"
    };
  }, [timeFilter, startDate, endDate, allSignals, activeSource, pipMult, currentTimezone]);

  const pipsPnl = activeBacktest.net_pnl || 0;

  const getTimeFilterLabel = () => {
    switch (timeFilter) {
      case "24h":
      case "today":
        return "Hôm nay (Từ 00:00)";
      case "3d": return "3 ngày qua";
      case "7d": return "7 ngày qua (1 tuần)";
      case "30d": return "30 ngày qua (1 tháng)";
      case "custom": return `Từ ${startDate} đến ${endDate}`;
      default:
        if (candleCount === 0) {
          return "Toàn bộ nến lưu trữ (Admin ALL)";
        }
        return `Toàn bộ ${candleCount >= 1000 ? `${(candleCount / 1000).toFixed(0)}k` : candleCount} nến tải về`;
    }
  };

  return (
    <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-4 sm:p-5 flex flex-col gap-4 shadow-2xl shadow-black/50 relative overflow-hidden">
      {/* Subtle top ambient glow */}
      <div className="absolute -top-20 -right-20 w-44 h-44 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Strategy Switcher Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800/80 pb-3">
        <div className="flex items-center gap-1 bg-slate-950/80 p-1 rounded-xl border border-slate-800/80 text-xs font-medium overflow-x-auto scrollbar-none shadow-inner max-w-full">
          {visibleSystems.map((sys) => {
            const Icon = sys.icon;
            const isSelected = strategyMode === sys.mode;
            return (
              <button
                key={sys.mode}
                onClick={() => handleSetStrategyMode(sys.mode)}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all duration-150 cursor-pointer shrink-0 ${
                  isSelected ? sys.activeBg : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isSelected ? (sys.iconColor || "text-current") : "text-slate-400"}`} />
                <span className="font-semibold">{sys.name}</span>
              </button>
            );
          })}

          {visibleSystems.length === 0 && (
            <div className="flex items-center gap-2 text-xs text-slate-400 px-2 py-0.5">
              <span>Chưa bật hệ thống</span>
              {onOpenIndicatorLibrary && (
                <button
                  type="button"
                  onClick={onOpenIndicatorLibrary}
                  className="text-cyan-400 hover:text-cyan-300 underline font-bold cursor-pointer"
                >
                  + Thêm
                </button>
              )}
            </div>
          )}

          {onOpenStrategySettings && visibleSystems.some((s) => s.mode === strategyMode) && (
            <button
              type="button"
              onClick={() => {
                const targetId =
                  strategyMode === "scalping_pro"
                    ? "scalping_pro_system"
                    : strategyMode === "liquidity_sweep"
                    ? "liquidity_sweep_pro"
                    : strategyMode === "orb_session"
                    ? "orb_session_system"
                    : strategyMode === "bob_volman"
                    ? "bob_volman_system"
                    : strategyMode === "matrix"
                    ? "matrix_pro"
                    : strategyMode === "smc"
                    ? "msb_dynamic"
                    : "hma_breakout_system";
                onOpenStrategySettings(targetId);
              }}
              title="Cấu hình & Tinh chỉnh chiến lược"
              className="p-1.5 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-slate-800/80 transition ml-0.5 cursor-pointer shrink-0"
            >
              <Settings className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Tabs: Tín Hiệu Live vs Backtest */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 bg-slate-950/80 p-1 rounded-xl border border-slate-800/80 text-xs font-semibold shadow-inner">
          <button
            onClick={() => handleSetActiveTab("signal")}
            className={`px-3.5 py-1.5 rounded-lg transition-all duration-150 cursor-pointer flex items-center gap-1.5 ${
              activeTab === "signal"
                ? "bg-slate-800/90 text-cyan-300 font-bold border border-cyan-500/30 shadow-md shadow-cyan-950/50"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40 font-medium"
            }`}
          >
            <Zap className="w-3.5 h-3.5 text-cyan-400" />
            <span>Tín Hiệu Live</span>
          </button>
          <button
            onClick={() => {
              if (!canAccess('BACKTEST_BASIC')) {
                promptTierGate('Báo Cáo Backtest Đa Khung', 'BASIC');
                return;
              }
              handleSetActiveTab("backtest");
            }}
            className={`px-3.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-all duration-150 cursor-pointer ${
              activeTab === "backtest"
                ? "bg-slate-800/90 text-emerald-300 font-bold border border-emerald-500/30 shadow-md shadow-emerald-950/50"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40 font-medium"
            }`}
          >
            <BarChart3 className="w-3.5 h-3.5 text-emerald-400" />
            <span>Backtest</span>
            {!canAccess('BACKTEST_BASIC') && <Lock className="w-3 h-3 text-amber-400 ml-0.5" />}
          </button>
        </div>

        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-950/70 border border-slate-800/80 text-[11px] font-mono text-slate-300 shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="font-semibold text-slate-200">{currentSymbol}</span>
          <span className="text-slate-600">•</span>
          <span className="text-cyan-400 font-bold">{currentTimeframe}</span>
        </div>
      </div>

      {!isBasic ? (
        <div className="rounded-xl p-5 border border-amber-500/30 bg-gradient-to-b from-amber-950/20 via-slate-900 to-slate-950 text-center space-y-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center mx-auto text-amber-400 shadow-inner">
            <Lock className="w-5 h-5" />
          </div>
          <div className="space-y-1">
            <h4 className="text-sm font-bold text-slate-100">Hệ Thống Giao Dịch Đang Khóa</h4>
            <p className="text-xs text-slate-400 max-w-xs mx-auto leading-relaxed">
              Đăng nhập tài khoản <strong className="text-amber-300">Basic (Miễn phí)</strong> để mở khóa Hệ Thống Giao Dịch SMC MSB, HMA Breakout & Matrix Pro (Tín hiệu Realtime, Giá Entry, TP/SL, R:R).
            </p>
          </div>
          <button
            onClick={() => openAuth('login')}
            className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-cyan-400 hover:from-cyan-400 hover:to-cyan-300 text-slate-950 font-bold text-xs rounded-lg shadow-md shadow-cyan-500/20 transition cursor-pointer"
          >
            Đăng Nhập Miễn Phí
          </button>
        </div>
      ) : visibleSystems.length === 0 ? (
        <div className="rounded-xl p-6 border border-slate-800 bg-slate-950/60 text-center space-y-3">
          <div className="w-10 h-10 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mx-auto text-cyan-400">
            <Layers className="w-5 h-5" />
          </div>
          <div className="space-y-1">
            <h4 className="text-sm font-bold text-slate-100">Chưa Bật Hệ Thống Giao Dịch Nào</h4>
            <p className="text-xs text-slate-400 max-w-xs mx-auto leading-relaxed">
              Vui lòng mở Thư Viện Chỉ Báo & Hệ Thống để thêm một hoặc nhiều hệ thống (SMC Pro, HMA Pro, Bob Volman, ORB Session, Matrix Pro) vào biểu đồ.
            </p>
          </div>
          {onOpenIndicatorLibrary && (
            <button
              onClick={onOpenIndicatorLibrary}
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-cyan-400 hover:from-cyan-400 hover:to-cyan-300 text-slate-950 font-bold text-xs rounded-lg shadow-md shadow-cyan-500/20 transition cursor-pointer"
            >
              Mở Thư Viện Chỉ Báo & Hệ Thống
            </button>
          )}
        </div>
      ) : activeTab === "signal" ? (
        <>
          {/* Strategy Quick Control: EMA 200 Mean Reversion Tool (Matrix Pro only) */}
          {strategyMode === "matrix" && (
            <div className="bg-slate-950/90 border border-slate-800/90 rounded-xl p-3 space-y-2.5 shadow-md">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="text-xs font-bold text-slate-100 flex items-center gap-1.5">
                    🎯 Lọc Sóng Hồi EMA 200 (Matrix Pro)
                  </span>
                </div>

                {/* Direct Toggle Switch on Panel */}
                <button
                  type="button"
                  onClick={() => {
                    const currentVal = strategyParams?.ema200DistFilterEnable !== false;
                    onApplyStrategy?.({ ema200DistFilterEnable: !currentVal });
                  }}
                  className={`px-2.5 py-0.5 rounded-full font-mono text-[11px] font-bold transition flex items-center gap-1.5 cursor-pointer border ${
                    strategyParams?.ema200DistFilterEnable !== false
                      ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/30"
                      : "bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${strategyParams?.ema200DistFilterEnable !== false ? "bg-emerald-400" : "bg-slate-500"}`} />
                  <span>{strategyParams?.ema200DistFilterEnable !== false ? "ĐANG BẬT" : "ĐANG TẮT"}</span>
                </button>
              </div>

              {/* Status and Distance Metric */}
              {signals?.ema200_info && (() => {
                const currentEmaInfo = signals?.ema200_info;
                return (
                <div className="bg-slate-900/90 rounded-lg p-2.5 border border-slate-800/80 space-y-2">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-400 text-[11px]">Trạng thái nến hiện tại:</span>
                    {currentEmaInfo.trend_lock_state === "REVERSAL_SELL_ONLY" ? (
                      <span className="px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 font-bold border border-rose-500/30 text-[10.5px] animate-pulse">
                        🌊 VỌT CAO: KHÓA CHỈ SELL (CẤM BUY)
                      </span>
                    ) : currentEmaInfo.trend_lock_state === "REVERSAL_BUY_ONLY" ? (
                      <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold border border-emerald-500/30 text-[10.5px] animate-pulse">
                        🌊 RỚT SÂU: KHÓA CHỈ BUY (CẤM SELL)
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded bg-cyan-500/15 text-cyan-300 font-bold border border-cyan-500/20 text-[10.5px]">
                        ⚖️ VÙNG TRUNG TÍNH (ĐÁNH 2 CHIỀU)
                      </span>
                    )}
                  </div>

                  {/* Real-time Distance vs Threshold */}
                  <div className="grid grid-cols-2 gap-2 text-[11px] font-mono pt-1 border-t border-slate-800/60">
                    <div className="bg-slate-950/60 p-1.5 rounded border border-slate-800/50">
                      <span className="text-[10px] text-slate-400 block font-sans">Đường EMA 200:</span>
                      <span className="text-amber-300 font-bold">{currentEmaInfo.ema200}</span>
                    </div>
                    <div className="bg-slate-950/60 p-1.5 rounded border border-slate-800/50">
                      <span className="text-[10px] text-slate-400 block font-sans">Khoảng cách Giá:</span>
                      <span className={`font-bold ${currentEmaInfo.distance_pips >= 0 ? "text-cyan-300" : "text-rose-300"}`}>
                        {currentEmaInfo.distance_pips > 0 ? `+${currentEmaInfo.distance_pips}` : currentEmaInfo.distance_pips} pips
                        {currentSymbol === "XAUUSD" && ` (${Math.abs(currentEmaInfo.distance_price || 0).toFixed(1)} giá)`}
                      </span>
                    </div>
                  </div>
                </div>
                );
              })()}

              {/* Quick Stepper Inputs for Outer Pips / Inner Pips */}
              {strategyParams?.ema200DistFilterEnable !== false && (
                <div className="grid grid-cols-2 gap-2 pt-0.5">
                  <div className="bg-slate-900/70 p-2 rounded-lg border border-slate-800 flex flex-col gap-1">
                    <span className="text-[10px] text-slate-400">Khóa sóng hồi (Xa EMA):</span>
                    <div className="flex items-center justify-between font-mono">
                      <button
                        type="button"
                        onClick={() => {
                          const val = Math.max(50, (strategyParams?.ema200OuterPips || 200) - 50);
                          onApplyStrategy?.({ ema200OuterPips: val });
                        }}
                        className="w-5 h-5 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center justify-center font-bold text-xs cursor-pointer"
                      >
                        -
                      </button>
                      <span className="text-xs font-bold text-cyan-300">
                        {strategyParams?.ema200OuterPips || 200}p {currentSymbol === "XAUUSD" && `(${((strategyParams?.ema200OuterPips || 200) / 10).toFixed(0)}g)`}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const val = Math.min(1000, (strategyParams?.ema200OuterPips || 200) + 50);
                          onApplyStrategy?.({ ema200OuterPips: val });
                        }}
                        className="w-5 h-5 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center justify-center font-bold text-xs cursor-pointer"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="bg-slate-900/70 p-2 rounded-lg border border-slate-800 flex flex-col gap-1">
                    <span className="text-[10px] text-slate-400">Mở 2 chiều (Gần EMA):</span>
                    <div className="flex items-center justify-between font-mono">
                      <button
                        type="button"
                        onClick={() => {
                          const val = Math.max(10, (strategyParams?.ema200InnerPips || 100) - 20);
                          onApplyStrategy?.({ ema200InnerPips: val });
                        }}
                        className="w-5 h-5 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center justify-center font-bold text-xs cursor-pointer"
                      >
                        -
                      </button>
                      <span className="text-xs font-bold text-emerald-300">
                        {strategyParams?.ema200InnerPips || 100}p {currentSymbol === "XAUUSD" && `(${((strategyParams?.ema200InnerPips || 100) / 10).toFixed(0)}g)`}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const val = Math.min(500, (strategyParams?.ema200InnerPips || 100) + 20);
                          onApplyStrategy?.({ ema200InnerPips: val });
                        }}
                        className="w-5 h-5 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center justify-center font-bold text-xs cursor-pointer"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Latest Trade Signal Box */}
          {latestSig ? (
            <div
              className={`rounded-2xl p-4 sm:p-5 border transition-all relative overflow-hidden backdrop-blur-md shadow-2xl ${
                isBuy
                  ? "bg-gradient-to-b from-slate-900/90 to-slate-950/95 border-emerald-500/30 hover:border-emerald-500/50 shadow-emerald-950/20"
                  : "bg-gradient-to-b from-slate-900/90 to-slate-950/95 border-rose-500/30 hover:border-rose-500/50 shadow-rose-950/20"
              }`}
            >
              {/* Subtle top ambient glow line */}
              <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${
                isBuy ? "via-emerald-500/40" : "via-rose-500/40"
              } to-transparent pointer-events-none`} />

              <div className="flex items-center justify-between mb-3.5">
                <div className="flex items-center gap-2.5">
                  <div
                    className={`p-2 rounded-xl shadow-sm ${
                      isBuy ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-rose-500/15 text-rose-400 border border-rose-500/30"
                    }`}
                  >
                    {isBuy ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-sm sm:text-base font-extrabold tracking-wide ${
                          isBuy ? "text-emerald-400" : "text-rose-400"
                        }`}
                      >
                        {latestSig.type || (isBuy ? "BUY SIGNAL" : "SELL SIGNAL")}
                      </span>
                      {latestSig.status === "WIN_TP" ? (
                        <span className="text-[10.5px] px-2 py-0.5 rounded-full font-mono font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-sm">
                          ✅ ĐÃ ĐẠT TP (+{latestSig.pnlPips !== undefined ? latestSig.pnlPips : (latestSig.pnl_pips ?? 0)}p)
                        </span>
                      ) : latestSig.status === "BE_WIN" ? (
                        <span className="text-[10.5px] px-2 py-0.5 rounded-full font-mono font-bold bg-teal-500/20 text-teal-300 border border-teal-500/40 shadow-sm">
                          🛡️ HÒA VỐN BE (+{latestSig.pnlPips !== undefined ? latestSig.pnlPips : (latestSig.pnl_pips ?? 0)}p)
                        </span>
                      ) : latestSig.status === "CLOSED_OPPOSITE" ? (
                        <span className={`text-[10.5px] px-2 py-0.5 rounded-full font-mono font-bold ${
                          (latestSig.pnlPips ?? latestSig.pnl_pips ?? 0) >= 0
                            ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                            : "bg-rose-500/20 text-rose-300 border border-rose-500/40"
                        }`}>
                          🔄 CẮT NGƯỢC CHIỀU ({(latestSig.pnlPips ?? latestSig.pnl_pips ?? 0) >= 0 ? "+" : ""}{latestSig.pnlPips !== undefined ? latestSig.pnlPips : (latestSig.pnl_pips ?? 0)}p)
                        </span>
                      ) : latestSig.status === "LOSS_SL" ? (
                        <span className="text-[10.5px] px-2 py-0.5 rounded-full font-mono font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40 shadow-sm">
                          ❌ ĐÃ CHẠM SL ({latestSig.pnlPips !== undefined ? latestSig.pnlPips : (latestSig.pnl_pips ?? 0)}p)
                        </span>
                      ) : latestSig.status === "PENDING_LIMIT" ? (
                        <span className="text-[10.5px] px-2 py-0.5 rounded-full font-mono font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 animate-pulse shadow-sm">
                          ⏳ CHỜ KHỚP LIMIT
                        </span>
                      ) : (latestSig.status === "TIMEOUT" || latestSig.status === "CANCELED") ? (
                        <span className="text-[10.5px] px-2 py-0.5 rounded-full font-mono font-bold bg-slate-800 text-slate-400 border border-slate-700">
                          ⏹ ĐÃ HỦY (TIMEOUT)
                        </span>
                      ) : latestSig.status === "SESSION_CLOSE" ? (
                        <span className={`text-[10.5px] px-2 py-0.5 rounded-full font-mono font-bold ${
                          (latestSig.pnlPips ?? latestSig.pnl_pips ?? 0) >= 0
                            ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                            : "bg-rose-500/20 text-rose-300 border border-rose-500/40"
                        }`}>
                          ⏹ HẾT PHIÊN ({(latestSig.pnlPips ?? latestSig.pnl_pips ?? 0) >= 0 ? "+" : ""}{latestSig.pnlPips !== undefined ? latestSig.pnlPips : (latestSig.pnl_pips ?? 0)}p)
                        </span>
                      ) : (
                        <span className="text-[10.5px] px-2 py-0.5 rounded-full font-mono font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 animate-pulse shadow-sm">
                          ⚡ ĐANG CHẠY {latestSig.pnlPips !== undefined && latestSig.pnlPips !== 0 ? `(${latestSig.pnlPips > 0 ? `+${latestSig.pnlPips}` : latestSig.pnlPips}p)` : ""}
                        </span>
                      )}
                      {latestSig.is_revert && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-mono font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                          🔄 REVERT
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] text-slate-400 block font-mono mt-0.5">
                      {strategyMode === "scalping_pro" ? `Scalping Pro (${latestSig.entry_type || "Smart Hybrid"} • ${latestSig.entry_mode || "M1"})` : strategyMode === "liquidity_sweep"
                        ? `Sweep Pro PA (${latestSig.sweep_type || "Stop Hunt"} • ${latestSig.entry_mode === "PULLBACK_50" ? "Limit 50%" : "Market MSS"})`
                        : strategyMode === "orb_session"
                        ? `ORB Session Pro (${latestSig.session_name || "Mở Phiên"} • ${latestSig.entry_mode === "LIMIT" ? "Limit 50%" : "Market Breakout"})`
                        : strategyMode === "bob_volman"
                        ? `Bob Volman Price Action (${latestSig.setup_name || latestSig.setup_type || "20 EMA Scalp"})`
                        : strategyMode === "smc"
                        ? (latestSig.is_revert ? `SMC Revert Fakeout (${latestSig.entry_mode || "LIMIT"})` : "SMC MSB Dynamic (Limit Entry)")
                        : strategyMode === "hma"
                        ? (latestSig.entry_mode === "LIMIT" ? "HMA Breakout Sniper (Limit Entry)" : "HMA Breakout Sniper (Market Entry)")
                        : "Matrix Strategy Pro"}
                    </span>
                  </div>
                </div>

                <div className="text-right font-mono shrink-0 pl-2">
                  <span className="text-[10px] text-slate-400 block uppercase font-sans font-semibold tracking-wider">Độ tin cậy</span>
                  <span className="text-sm font-extrabold text-amber-400">{latestSig.confidence || 88}%</span>
                </div>
              </div>

              {/* Trade Parameters (Entry, TP1/TP2/TP3, SL) */}
              <div className="grid grid-cols-3 gap-2 sm:gap-2.5 bg-slate-950/70 p-3 rounded-xl border border-slate-800/80 font-mono text-xs shadow-inner">
                <div>
                  <div className="h-5 sm:h-6 flex items-center">
                    <span className="text-slate-400 text-[10px] block font-sans font-semibold uppercase tracking-wider truncate">
                      {strategyMode === "smc" ? (latestSig.is_revert ? (latestSig.entry_mode === "MARKET" ? "GIÁ MARKET" : "GIÁ LIMIT") : "GIÁ LIMIT") : latestSig.entry_mode === "LIMIT" || latestSig.entry_mode === "PULLBACK_50" ? "GIÁ LIMIT (50%)" : "GIÁ VÀO"}
                    </span>
                  </div>
                  <span className="font-extrabold text-slate-100 text-xs sm:text-sm mt-0.5 block">{formatPrice(latestSig.price)}</span>
                </div>
                <div>
                  <div className="h-5 sm:h-6 flex items-center">
                    <span className="text-emerald-400 text-[10px] flex items-center gap-1 font-sans font-semibold uppercase tracking-wider truncate">
                      <Target className="w-3 h-3 text-emerald-400 shrink-0" /> TP Mục Tiêu
                    </span>
                  </div>
                  <span className="font-extrabold text-emerald-400 text-xs sm:text-sm mt-0.5 block">
                    {formatPrice(latestSig.tp || latestSig.tp2 || latestSig.tp1)} {(strategyMode === "smc" || strategyMode === "bob_volman") && latestSig.tp2 ? `• ${formatPrice(latestSig.tp2)}` : ""}
                  </span>
                </div>
                <div>
                  <div className="h-5 sm:h-6 flex items-center">
                    <span className="text-rose-400 text-[10px] flex items-center gap-1 font-sans font-semibold uppercase tracking-wider truncate">
                      <ShieldCheck className="w-3 h-3 text-rose-400 shrink-0" /> Cắt Lỗ (SL)
                    </span>
                  </div>
                  <span className="font-extrabold text-rose-400 text-xs sm:text-sm mt-0.5 block">{formatPrice(latestSig.sl)}</span>
                </div>
              </div>

              {/* 2 TP Target Breakdown for MSB Dynamic & Bob Volman */}
              {((strategyMode === "smc" && latestSig.tp2) || (strategyMode === "bob_volman" && latestSig.tp2)) && (
                <div className="mt-2 flex flex-wrap items-center justify-between text-[10px] font-mono bg-slate-950/50 px-2.5 py-1 rounded border border-slate-800/60 text-slate-300">
                  {strategyMode === "liquidity_sweep" && (
                    <span>Sweep: <strong className="text-violet-400">{latestSig.sweep_type || "SWEEP"} ({latestSig.sweep_pips || 0}p)</strong></span>
                  )}
                  {strategyMode === "bob_volman" && (
                    <span>Setup: <strong className="text-cyan-400">{latestSig.setup_name || latestSig.setup_type || "BB"}</strong></span>
                  )}
                  <span>TP1: <strong className="text-emerald-400">{formatPrice(latestSig.tp1)}</strong></span>
                  <span>TP2: <strong className="text-emerald-300">{formatPrice(latestSig.tp2)}</strong></span>
                  <span className="text-slate-400">R:R: <strong className="text-cyan-300">{latestSig.rr_ratio || "1:2.0"}</strong></span>
                  {strategyMode === "liquidity_sweep" && latestSig.mss_level && (
                    <span className="text-slate-400">MSS: <strong className="text-amber-300">{formatPrice(latestSig.mss_level)}</strong></span>
                  )}
                  {strategyMode === "bob_volman" && latestSig.buildup_candles && (
                    <span className="text-slate-400">Nén: <strong className="text-amber-300">{latestSig.buildup_candles} nến</strong></span>
                  )}
                </div>
              )}

              {/* ORB Box Range Info */}
              {strategyMode === "orb_session" && latestSig.box_high && latestSig.box_low && (
                <div className="mt-2 flex flex-wrap items-center justify-between text-[10px] font-mono bg-amber-950/30 px-2.5 py-1 rounded border border-amber-800/40 text-slate-300">
                  <span>Session: <strong className="text-amber-400">{latestSig.session_name || "Mở Phiên"}</strong></span>
                  <span>Box High: <strong className="text-emerald-400">{formatPrice(latestSig.box_high)}</strong></span>
                  <span>Box Low: <strong className="text-rose-400">{formatPrice(latestSig.box_low)}</strong></span>
                  <span>RR: <strong className="text-cyan-300">{latestSig.rr_ratio || "1:3.0"}</strong></span>
                </div>
              )}

            </div>
          ) : (
            <div className="p-6 text-center text-slate-400 bg-slate-950/50 rounded-xl border border-slate-800 text-xs">
              <Clock className="w-6 h-6 mx-auto mb-2 text-slate-500" />
              <p>Chưa có tín hiệu giao dịch mới cho {currentSymbol} ({currentTimeframe}).</p>
            </div>
          )}

          {/* Historical Signals / Orders Table (Limited to 10 for Free/Basic, Full in PRO Modal) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <span>
                  {strategyMode === "liquidity_sweep"
                    ? "Danh sách lệnh Liquidity Sweep Pro (PA)"
                    : strategyMode === "orb_session"
                    ? "Danh sách lệnh ORB Session Pro"
                    : strategyMode === "bob_volman"
                    ? "Danh sách lệnh Bob Volman Price Action"
                    : strategyMode === "smc"
                    ? "Danh sách lệnh SMC"
                    : strategyMode === "hma"
                    ? "Danh sách lệnh HMA Breakout"
                    : "Lịch sử tín hiệu"}
                </span>
                <span className="text-[10px] text-slate-500 font-mono font-normal">
                  (10 lệnh gần nhất)
                </span>
              </h4>

              <button
                onClick={() => {
                  if (isPro) {
                    setIsHistoryModalOpen(true);
                  } else {
                    promptTierGate("Lịch Sử Lệnh Chi Tiết Toàn Bộ", "PRO");
                  }
                }}
                className="px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 transition cursor-pointer shadow-sm"
                title="Xem toàn bộ lịch sử chi tiết theo ngày giờ (Dành cho gói PRO)"
              >
                <History className="w-3.5 h-3.5 text-amber-400" />
                <span>Xem đầy đủ</span>
                {!isPro && <Lock className="w-3 h-3 text-amber-400" />}
              </button>
            </div>

            <div className="space-y-1.5 pr-0.5">
              {allSignals.slice().reverse().slice(0, 10).map((sig) => {
                const diff = sig.pnl_diff || 0.0;
                const pips = sig.pnlPips !== undefined ? sig.pnlPips : (sig.pnl_pips !== undefined ? sig.pnl_pips : Math.round(diff * pipMult * 10) / 10);
                const isRunning = sig.status === "RUNNING" || sig.status === "PENDING" || sig.status === "FILLED" || sig.status === "PENDING_LIMIT" || sig.status === "OPEN";
                const isCanceled = Boolean(sig.canceled) || (typeof sig.status === "string" && (sig.status.startsWith("CANCELED") || sig.status === "EXPIRED")) || (sig.status === "TIMEOUT" && pips === 0 && !sig.exit_price);
                const isBE = !isCanceled && (sig.status === "BREAKEVEN" || sig.status === "BE" || sig.status === "BE_WIN" || sig.status === "BE_OPPOSITE" || (diff === 0 && pips === 0 && !isRunning && sig.status !== "TIMEOUT" && sig.status !== "SESSION_CLOSE"));
                const isWin = !isBE && !isCanceled && (sig.status === "WIN_TP" || sig.status === "WIN_TP1" || sig.status === "WIN_TP2" || diff > 0 || pips > 0);
                const isSessionClose = sig.status === "SESSION_CLOSE" || (sig.status === "TIMEOUT" && (pips !== 0 || sig.exit_price));

                return (
                  <div
                    key={sig.id || sig.order_id}
                    className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-xl bg-slate-900/50 hover:bg-slate-900/90 border border-slate-800/60 hover:border-slate-700/80 text-xs font-mono transition-all shadow-sm"
                  >
                    {/* Action & Entry */}
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          sig.action === "BUY" ? "bg-emerald-400" : "bg-rose-400"
                        }`}
                      />
                      <span
                        className={`font-bold text-[11px] ${
                          sig.action === "BUY" ? "text-emerald-400" : "text-rose-400"
                        }`}
                      >
                        {sig.action}
                      </span>
                      {sig.setup_type && (
                        <span className="px-1.5 py-0.5 rounded text-[9.5px] font-bold bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                          {sig.setup_type}
                        </span>
                      )}
                      <span className="text-slate-300 text-[11px]">@{formatPrice(sig.price || sig.entry)}</span>
                    </div>

                    {/* TP & SL targets + Pips Outcome */}
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="text-emerald-400/80">TP: {formatPrice(sig.tp1 || sig.tp)}</span>
                      <span className="text-rose-400/80">SL: {formatPrice(sig.sl)}</span>

                      {/* +/- Pips Tag */}
                      {isCanceled ? (
                        <span className="px-2 py-0.5 rounded-lg text-[10px] font-medium bg-slate-800 text-slate-400 border border-slate-700">
                          {sig.status === "CANCELED_TP_HIT" ? "Hủy (Đạt TP trước)" : (sig.status === "EXPIRED" ? "Hết hạn (Limit)" : "Hủy (Timeout)")}
                        </span>
                      ) : isSessionClose ? (
                        <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold ${
                          pips >= 0
                            ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                            : "bg-rose-500/20 text-rose-300 border border-rose-500/40"
                        }`}>
                          Hết phiên ({pips >= 0 ? `+${pips}` : pips}p)
                        </span>
                      ) : isRunning ? (
                        <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 animate-pulse">
                          {sig.status === "PENDING_LIMIT" || sig.status === "PENDING"
                            ? "Chờ khớp"
                            : (pips !== 0 ? `Đang chạy (${pips > 0 ? `+${pips}` : pips}p)` : "Đang chạy")}
                        </span>
                      ) : sig.status === "CLOSED_OPPOSITE" ? (
                        <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold ${
                          pips >= 0
                            ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                            : "bg-rose-500/20 text-rose-300 border border-rose-500/40"
                        }`}>
                          Cắt ngược ({pips >= 0 ? `+${pips}` : pips}p)
                        </span>
                      ) : sig.status === "BE_WIN" ? (
                        <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-emerald-500/20 text-teal-300 border border-teal-500/40">
                          Hòa vốn ({pips >= 0 ? `+${pips}` : pips}p)
                        </span>
                      ) : isBE ? (
                        <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                          Hòa vốn (0.0p)
                        </span>
                      ) : (
                        <span
                          className={`font-bold px-2 py-0.5 rounded-lg text-[10.5px] ${
                            isWin
                              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                              : "bg-rose-500/20 text-rose-400 border border-rose-500/40"
                          }`}
                        >
                          {pips >= 0 ? `+${pips}` : `${pips}`} pips
                        </span>
                      )}

                      {/* Time */}
                      <span className="text-slate-500 font-sans text-[10px]">
                        {formatDisplayTime(sig.time, currentTimezone || "local", "time")}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* View Full History Button at bottom of list */}
            {allSignals.length > 10 && (
              <button
                onClick={() => {
                  if (isPro) {
                    setIsHistoryModalOpen(true);
                  } else {
                    promptTierGate("Lịch Sử Lệnh Chi Tiết Toàn Bộ", "PRO");
                  }
                }}
                className="w-full py-2 px-3 rounded-lg border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-slate-900 to-amber-500/10 hover:from-amber-500/20 hover:to-amber-500/20 text-amber-300 text-xs font-bold flex items-center justify-center gap-2 transition cursor-pointer shadow-sm group"
              >
                <History className="w-3.5 h-3.5 text-amber-400 group-hover:rotate-45 transition-transform" />
                <span>Xem đầy đủ toàn bộ {allSignals.length} lệnh trong lịch sử</span>
                {!isPro ? (
                  <span className="px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 text-[10px] font-mono border border-amber-500/40 flex items-center gap-1">
                    <Lock className="w-2.5 h-2.5" /> PRO
                  </span>
                ) : (
                  <span className="text-[10px] text-amber-400 font-mono">→</span>
                )}
              </button>
            )}
          </div>
        </>
      ) : (
        /* Standardized PIPS Backtesting View with Custom Date Range Filter */
        <div className="space-y-3 animate-fadeIn">
          {/* Preset Buttons + Custom Date Toggle */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1 bg-slate-950/80 p-1 rounded-xl border border-slate-800/80 text-xs font-medium overflow-x-auto scrollbar-none shadow-inner max-w-full">
              {[
                { id: "today", label: "Hôm Nay" },
                { id: "3d", label: "3 Ngày" },
                { id: "7d", label: "7 Ngày" },
                { id: "30d", label: "30 Ngày" },
              ].map((tf) => (
                <button
                  key={tf.id}
                  onClick={() => handleSetTimeFilter(tf.id)}
                  className={`px-2.5 py-1 rounded-lg transition-all duration-150 cursor-pointer text-xs ${
                    (timeFilter === "24h" && tf.id === "today") || timeFilter === tf.id
                      ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold shadow-sm shadow-emerald-500/10"
                      : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40"
                  }`}
                >
                  {tf.label}
                </button>
              ))}
              <button
                onClick={() => {
                  if (!isPro && candleCount > 10000) {
                    promptTierGate("Backtest Dữ Liệu Chuyên Sâu (> 10k nến)", "PRO");
                    return;
                  }
                  handleSetTimeFilter("all");
                }}
                className={`px-2.5 py-1 rounded-lg transition-all duration-150 cursor-pointer flex items-center gap-1.5 text-xs ${
                  timeFilter === "all"
                    ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold shadow-sm shadow-emerald-500/10"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40"
                }`}
              >
                {!isPro && candleCount > 10000 && <Lock className="w-2.5 h-2.5 text-amber-400" />}
                <span>Toàn Bộ ({candleCount >= 1000 ? `${(candleCount / 1000).toFixed(0)}k` : candleCount})</span>
              </button>
            </div>

            <button
              onClick={() => handleSetTimeFilter("custom")}
              className={`px-3 py-1.5 rounded-xl flex items-center gap-1.5 transition-all duration-150 cursor-pointer text-xs font-semibold ${
                timeFilter === "custom"
                  ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-sm shadow-cyan-500/10"
                  : "bg-slate-950/70 hover:bg-slate-800/60 text-slate-300 border border-slate-800/80"
              }`}
            >
              <CalendarRange className="w-3.5 h-3.5 text-cyan-400" />
              <span>Chọn ngày</span>
            </button>
          </div>

          {/* Interactive Date Range Inputs when "custom" is active */}
          {timeFilter === "custom" && (
            <div className="bg-slate-950/80 p-3 rounded-xl border border-cyan-500/30 flex flex-wrap items-center justify-between gap-2.5 text-xs shadow-inner">
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-[11px] font-medium">Từ ngày:</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1 text-slate-100 text-xs focus:outline-none focus:border-cyan-500 transition"
                />
              </div>

              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-[11px] font-medium">Đến ngày:</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-slate-900 border border-slate-700/80 rounded-lg px-2.5 py-1 text-slate-100 text-xs focus:outline-none focus:border-cyan-500 transition"
                />
              </div>
            </div>
          )}

          {/* Performance Card in PIPS with Streaks */}
          <div className="bg-gradient-to-b from-slate-900/90 to-slate-950/95 p-4 sm:p-5 rounded-2xl border border-slate-800/90 shadow-2xl relative overflow-hidden flex flex-col gap-4">
            {/* Subtle top ambient glow line */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent pointer-events-none" />

            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 shadow-sm shrink-0">
                  <Award className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <h4 className="text-xs sm:text-[13px] font-bold text-slate-100 uppercase tracking-wide truncate">
                    {strategyMode === "scalping_pro" ? "Hiệu Suất Scalping Pro" : strategyMode === "liquidity_sweep" ? "Hiệu Suất Liquidity Sweep Pro" : strategyMode === "orb_session" ? "Hiệu Suất ORB Session Pro" : strategyMode === "bob_volman" ? "Hiệu Suất Bob Volman Pro" : strategyMode === "smc" ? "Hiệu Suất SMC MSB Dynamic" : strategyMode === "hma" ? "Hiệu Suất HMA Breakout Pro" : "Hiệu Suất Matrix Pro"}
                  </h4>
                  <div className="text-[11px] text-slate-400 font-mono flex items-center gap-1.5 mt-0.5 truncate">
                    <span>{getTimeFilterLabel()}</span>
                    <span className="text-slate-600">•</span>
                    <span>{(strategyMode === "liquidity_sweep" || strategyMode === "scalping_pro") ? "Chốt TP" : "Chốt TP1"} (RR {activeBacktest.avg_rr || ((strategyMode === "liquidity_sweep" || strategyMode === "scalping_pro") ? "1:2.0" : "1:1.0")})</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end shrink-0 pl-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-sans font-semibold">Tỷ lệ thắng</span>
                <span className="font-mono text-base sm:text-lg font-extrabold text-emerald-400 drop-shadow-sm">
                  {activeBacktest.win_rate || 0}%
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 font-mono text-xs">
              <div className="p-3 rounded-xl bg-slate-900/60 hover:bg-slate-900/90 border border-slate-800/80 hover:border-slate-700/80 transition-all flex flex-col justify-between shadow-sm">
                <div className="h-7 sm:h-8 flex items-start">
                  <span className="text-slate-400 text-[10px] font-sans font-semibold uppercase tracking-wider leading-tight">
                    Tổng số lệnh
                  </span>
                </div>
                <div className="h-7 flex items-center">
                  <span className="font-extrabold text-slate-100 text-sm sm:text-base font-mono">
                    {activeBacktest.total_trades || 0}
                  </span>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-slate-900/60 hover:bg-slate-900/90 border border-slate-800/80 hover:border-slate-700/80 transition-all flex flex-col justify-between shadow-sm">
                <div className="h-7 sm:h-8 flex items-start">
                  <span className="text-slate-400 text-[10px] font-sans font-semibold uppercase tracking-wider leading-tight">
                    Thắng / Thua
                  </span>
                </div>
                <div className="h-7 flex items-center font-extrabold text-sm sm:text-base font-mono">
                  <span className="text-emerald-400">{activeBacktest.wins || 0}</span>
                  <span className="text-slate-600 font-normal mx-0.5">/</span>
                  <span className="text-rose-400">{activeBacktest.losses || 0}</span>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-slate-900/60 hover:bg-slate-900/90 border border-slate-800/80 hover:border-slate-700/80 transition-all flex flex-col justify-between shadow-sm">
                <div className="h-7 sm:h-8 flex items-start">
                  <span className="text-slate-400 text-[10px] font-sans font-semibold uppercase tracking-wider leading-tight">
                    Profit Factor
                  </span>
                </div>
                <div className="h-7 flex items-center">
                  <span className="font-extrabold text-amber-400 text-sm sm:text-base font-mono">
                    {activeBacktest.profit_factor || 1.0}
                  </span>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-slate-900/60 hover:bg-slate-900/90 border border-slate-800/80 hover:border-slate-700/80 transition-all flex flex-col justify-between shadow-sm">
                <div className="h-7 sm:h-8 flex items-start">
                  <span className="text-slate-400 text-[10px] font-sans font-semibold uppercase tracking-wider leading-tight">
                    Lợi nhuận (Pips)
                  </span>
                </div>
                <div className="h-7 flex items-center">
                  <span className={`font-extrabold text-sm sm:text-base font-mono truncate ${pipsPnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {pipsPnl >= 0 ? "+" : ""}{pipsPnl.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Pine Script Streaks Card */}
            <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-slate-800/80 font-mono text-xs">
              <div className="p-2.5 rounded-xl bg-emerald-500/5 border border-emerald-500/20 flex items-center justify-between">
                <span className="text-emerald-300 text-[11px] font-sans flex items-center gap-1.5 font-medium">
                  <Flame className="w-3.5 h-3.5 text-emerald-400" /> Chuỗi Thắng Max:
                </span>
                <span className="font-extrabold text-emerald-400 text-xs font-mono">
                  {(activeBacktest.max_win_streak ?? activeBacktest.streaks?.max_win_streak ?? 0)} lệnh
                </span>
              </div>

              <div className="p-2.5 rounded-xl bg-rose-500/5 border border-rose-500/20 flex items-center justify-between">
                <span className="text-rose-300 text-[11px] font-sans flex items-center gap-1.5 font-medium">
                  <Flame className="w-3.5 h-3.5 text-rose-400" /> Chuỗi Thua Max:
                </span>
                <span className="font-extrabold text-rose-400 text-xs font-mono">
                  {(activeBacktest.max_loss_streak ?? activeBacktest.streaks?.max_loss_streak ?? 0)} lệnh
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PRO Detailed Trade History Modal */}
      {isHistoryModalOpen && (
        <TradeHistoryModal
          isOpen={isHistoryModalOpen}
          onClose={() => setIsHistoryModalOpen(false)}
          signals={allSignals}
          currentSymbol={currentSymbol}
          currentTimeframe={currentTimeframe}
          currentTimezone={currentTimezone}
          strategyMode={strategyMode}
          pipMultiplier={pipMult}
        />
      )}
    </div>
  );
}